/**
 * Format SnapTrade/broker order failure messages for user display.
 * Strips " RESPONSE HEADERS: ..." noise and applies consistent capitalization.
 */

/**
 * Strip response headers and axios noise from raw error message.
 * Returns a short, meaningful message (e.g. "Order rejected by broker (400)." when only status line remains).
 */
export function formatFailureReason(raw: string | undefined | null): string {
  if (!raw || typeof raw !== 'string') return 'No reason provided';
  let msg = raw;
  const headersIdx = msg.indexOf(' RESPONSE HEADERS:');
  if (headersIdx !== -1) msg = msg.substring(0, headersIdx).trim();
  const statusMatch = msg.match(/Request failed with status code (\d+)/i);
  if (statusMatch && msg.length > 80) return `Order rejected by broker (${statusMatch[1]}).`;
  return msg || 'No reason provided';
}

/**
 * Capitalize first letter and fix common error words (e.g. "insufficient" -> "Insufficient").
 */
export function formatFailureReasonWithRules(reason: string): string {
  if (!reason || !reason.trim()) return reason;
  const rules: [RegExp | string, string][] = [
    [/\binsufficient\b/gi, 'Insufficient'],
    [/\bauthentication failed\b/gi, 'Authentication failed'],
    [/\border validation failed\b/gi, 'Order validation failed'],
    [/\baccount or symbol not found\b/gi, 'Account or symbol not found'],
    [/\brate limited\b/gi, 'Rate limited'],
    [/\btrading permission denied\b/gi, 'Trading permission denied'],
    [/\binvalid order parameters\b/gi, 'Invalid order parameters'],
  ];
  let out = reason.trim();
  for (const [from, to] of rules) {
    out = typeof from === 'string' ? out.replace(from, to) : out.replace(from, to);
  }
  return out.charAt(0).toUpperCase() + out.slice(1);
}

/**
 * Format raw trade/order error for display (combines formatFailureReason + formatFailureReasonWithRules).
 */
export function formatTradeErrorForDisplay(raw: string | undefined | null): string {
  return formatFailureReasonWithRules(formatFailureReason(raw));
}
